/**
 * HourlySummaryView — Summary statistics for the Hourly Summary Report.
 *
 * Displays:
 *   1. Key Response Time Metrics (5 stat cards)
 *   2. Top 5 Most Active Residents table
 *   3. Hourly Breakdown table (24 rows)
 *
 * Props:
 *   summaryData    — array of 24 hourly aggregate objects
 *   hourlyStats    — { max, min, avg, savg, mostActive }
 *   startDisplay   — formatted start datetime string
 *   endDisplay     — formatted end datetime string
 */

import { useMemo } from 'react';
import { Link } from 'react-router-dom';

const HourlySummaryView = ({ summaryData = [], hourlyStats = {}, startDisplay, endDisplay, start, end, reportFilters }) => {
    const { max, min, avg, savg, mostActive = [] } = hourlyStats;

    // Calculate total events from hourly rows
    const totalEvents = useMemo(() => {
        return summaryData.reduce((sum, row) => sum + (parseInt(row.count, 10) || 0), 0);
    }, [summaryData]);

    const formatMetric = (val) => {
        if (val == null) return '\u2014';
        return val;
    };

    /* `== null` ALONE, matching AlertSummaryView. savg and each hour's
       avg_service_time come from sp_getHourlySummary / ...Cloud, which returns
       NULL for an hour with nothing measurable (Phase 4, H3). The Lambda passes
       that through as None instead of coercing it to 0.0, so the zero arms would
       now misreport a real sub-three-second service time as "no measurement". */
    const formatServiceTime = (val) => {
        if (val == null || val === '') return '-';
        return val;
    };

    return (
        <div className="ar-summary">
            {/* Date range label */}
            <p className="ar-summary__range">
                {startDisplay} &mdash; {endDisplay}
            </p>

            {/* Section 1: Key Response Time Metrics */}
            <div className="hs-metrics-row">
                <div className="ar-summary-card">
                    <div className="ar-summary-card__value">{totalEvents.toLocaleString()}</div>
                    <div className="ar-summary-card__label">Total Events</div>
                </div>
                <div className="ar-summary-card">
                    <div className="ar-summary-card__value">{formatMetric(max)}</div>
                    <div className="ar-summary-card__label">Longest Response (min)</div>
                </div>
                <div className="ar-summary-card">
                    <div className="ar-summary-card__value">{formatMetric(min)}</div>
                    <div className="ar-summary-card__label">Shortest Response (min)</div>
                </div>
                <div className="ar-summary-card">
                    <div className="ar-summary-card__value">{formatMetric(avg)}</div>
                    <div className="ar-summary-card__label">Avg Response (min)</div>
                </div>
                <div className="ar-summary-card">
                    <div className="ar-summary-card__value">{formatServiceTime(savg)}</div>
                    <div className="ar-summary-card__label">Avg Service (min)</div>
                </div>
            </div>

            {/* Section 2: Top 5 Most Active Residents */}
            {mostActive.length > 0 ? (
                <div className="hs-top-residents">
                    <div className="pp-section">
                        <h4 className="pp-section__title">Top 5 Most Active Residents</h4>
                        <div className="dp-table-wrap">
                            <table className="dp-table">
                                <thead className="dp-thead">
                                    <tr>
                                        <th className="dp-th">Resident</th>
                                        <th className="dp-th dp-th--center">Event Count</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {mostActive.map((r, idx) => (
                                        <tr key={idx} className="dp-tr">
                                            <td className="dp-td">
                                                {r.resident_id ? (
                                                    <Link to={`/profiles/resident/${r.resident_id}`} className="dp-link">
                                                        {r.Resident || r.resident_name || 'Unknown'}
                                                    </Link>
                                                ) : (
                                                    <span className="dp-cell-text">
                                                        {r.Resident || r.resident_name || 'Unknown'}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="dp-td dp-td--center">
                                                <span className="dp-cell-text">
                                                    {r.Count != null ? r.Count.toLocaleString() : '0'}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            ) : null}

            {/* Section 3: Hourly Breakdown Table */}
            <div className="hs-hourly-section">
                <div className="pp-section">
                    <h4 className="pp-section__title">Hourly Breakdown</h4>
                    <div className="dp-table-wrap">
                        <table className="dp-table">
                            <thead className="dp-thead">
                                <tr>
                                    <th className="dp-th">Hour of Day</th>
                                    <th className="dp-th dp-th--center"># Events</th>
                                    <th className="dp-th dp-th--center">Avg Response (min)</th>
                                    <th className="dp-th dp-th--center">Min Response (min)</th>
                                    <th className="dp-th dp-th--center">Max Response (min)</th>
                                    <th className="dp-th dp-th--center">Avg Service Time (min)</th>
                                </tr>
                            </thead>
                            <tbody>
                                {summaryData.length > 0 ? summaryData.map((row, idx) => (
                                    <tr key={idx} className="dp-tr">
                                        <td className="dp-td">
                                            <span className="dp-cell-text">{row.display_hour || '\u2014'}</span>
                                        </td>
                                        <td className="dp-td dp-td--center">
                                            {parseInt(row.count, 10) > 0 ? (
                                                <Link
                                                    to="/reports/alerts/run"
                                                    state={{
                                                        startDate: start,
                                                        endDate: end,
                                                        hour: row.hour,
                                                        filters: reportFilters,
                                                    }}
                                                    className="dp-link"
                                                    title="View alert details for this hour"
                                                >
                                                    {row.count}
                                                </Link>
                                            ) : (
                                                <span className="dp-cell-text">{row.count || '0'}</span>
                                            )}
                                        </td>
                                        <td className="dp-td dp-td--center">
                                            <span className="dp-cell-text">{row.avg_response_time ?? '\u2014'}</span>
                                        </td>
                                        <td className="dp-td dp-td--center">
                                            <span className="dp-cell-text">{row.min_response_time ?? '\u2014'}</span>
                                        </td>
                                        <td className="dp-td dp-td--center">
                                            <span className="dp-cell-text">{row.max_response_time ?? '\u2014'}</span>
                                        </td>
                                        <td className="dp-td dp-td--center">
                                            <span className="dp-cell-text">
                                                {formatServiceTime(row.avg_service_time)}
                                            </span>
                                        </td>
                                    </tr>
                                )) : (
                                    <tr className="dp-tr">
                                        <td className="dp-td dp-td--center" colSpan={6} style={{ padding: 'var(--space-6)' }}>
                                            <span className="dp-cell-text dp-cell-text--muted">No hourly data for this period.</span>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HourlySummaryView;
